import { propOr } from 'ramda'
import { getSubjectLabelMap } from 'utils/subjects'
import { getSubjectExpansion } from 'utils/messageMaps'

const DAY_NAME_TO_NUMBER = {
  Poniedziałek: 1,
  Wtorek: 2,
  Środa: 3,
  Czwartek: 4,
  Piątek: 5,
  Sobota: 6,
  Niedziela: 7
}

export const WEEKDAY_OPTIONS = [
  { label: '--', value: '' },
  { label: 'pon.', value: 1 },
  { label: 'wt.', value: 2 },
  { label: 'śr.', value: 3 },
  { label: 'czw.', value: 4 },
  { label: 'pt.', value: 5 },
  { label: 'sob.', value: 6 },
  { label: 'nd.', value: 7 }
]

export const normalizeDay = day => {
  if (day == null || day === '') return ''
  if (typeof day === 'number' && day >= 1 && day <= 7) return day
  return DAY_NAME_TO_NUMBER[day] ?? ''
}

export const parseAdditionalData = student => {
  try {
    const raw = propOr('', 'additionalData', student)
    if (!raw) return { subjects: [] }
    const parsed = JSON.parse(raw)
    return { subjects: Array.isArray(parsed?.subjects) ? parsed.subjects : [] }
  } catch {
    return { subjects: [] }
  }
}

const subjectMatches = (s, teacher, subjectLabel) => {
  const key = (teacher.subject || '').trim().toLowerCase()
  const labelKey = (subjectLabel || '').trim().toLowerCase()
  const sSubj = (s.subject || '').trim().toLowerCase()
  const sLabel = (s.subjectLabel || '').trim().toLowerCase()
  return sSubj === key || sLabel === key || sSubj === labelKey || sLabel === labelKey
}

const getResolvedSubjectLabel = (teacher, schoolSubjects) => {
  const labelMap = getSubjectLabelMap(schoolSubjects)
  return labelMap[teacher.subject]?.long ?? getSubjectExpansion(teacher.subject) ?? teacher.subject ?? ''
}

/**
 * Wpis additionalData należy do tego wiersza nauczyciela.
 * teacherEntryId rozróżnia dwóch nauczycieli z tym samym przedmiotem; bez niego — dopasowanie po przedmiocie (stare dane).
 */
const entryMatchesTeacher = (s, teacher, subjectLabel) => {
  if (teacher.entryId) {
    if (s.teacherEntryId === teacher.entryId) return true
    if (subjectMatches(s, teacher, subjectLabel) && !s.teacherEntryId) return true
    return false
  }
  return subjectMatches(s, teacher, subjectLabel)
}

export const additionalSubjectEntryMatchesTeacher = (entry, teacher, schoolSubjects) => {
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  return entryMatchesTeacher(entry, teacher, subjectLabel)
}

const findEntryIndexForTeacher = (subjects, teacher, schoolSubjects) => {
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  return subjects.findIndex(s => entryMatchesTeacher(s, teacher, subjectLabel))
}

const withTeacherEntryMeta = (teacher, fields) =>
  teacher.entryId ? { ...fields, teacherEntryId: teacher.entryId } : fields

export const getEntryForTeacher = (teacher, additionalSubjects, schoolSubjects) => {
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  return (additionalSubjects || []).find(s => entryMatchesTeacher(s, teacher, subjectLabel))
}

const getSlotsFromEntry = entry => {
  if (!entry) return []
  if (entry.slots && Array.isArray(entry.slots)) return entry.slots
  if (entry.day != null || entry.time) return [{ day: entry.day ?? '', time: entry.time || '' }]
  return []
}

export const getSlotsForTeacher = (teacher, additionalSubjects, schoolSubjects) => {
  const entry = getEntryForTeacher(teacher, additionalSubjects, schoolSubjects)
  const slots = getSlotsFromEntry(entry)
  return slots.length ? slots : [{ day: '', time: '' }]
}

export const mergeSlot = (current, teacher, slotIndex, day, time, schoolSubjects) => {
  const subjects = [...(current?.subjects || [])]
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  const idx = findEntryIndexForTeacher(subjects, teacher, schoolSubjects)

  const normalizedSlot = {
    day: day === '' || day == null ? '' : Number(day) || day,
    time: (time || '').trim()
  }

  if (idx >= 0) {
    const entry = subjects[idx]
    const slots = [...getSlotsFromEntry(entry)]
    if (!slots.length) slots.push({ day: '', time: '' })
    slots[slotIndex] = normalizedSlot
    subjects[idx] = withTeacherEntryMeta(teacher, { ...entry, subject: teacher.subject || '', subjectLabel, slots })
  } else {
    const slots = Array.from({ length: slotIndex + 1 }, () => ({ day: '', time: '' }))
    slots[slotIndex] = normalizedSlot
    subjects.push(withTeacherEntryMeta(teacher, { subject: teacher.subject || '', subjectLabel, slots }))
  }
  return { subjects }
}

export const addSlotForTeacher = (current, teacher, schoolSubjects) => {
  const subjects = [...(current?.subjects || [])]
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  const idx = findEntryIndexForTeacher(subjects, teacher, schoolSubjects)

  const newSlot = { day: '', time: '' }

  if (idx >= 0) {
    const entry = subjects[idx]
    const slots = [...getSlotsFromEntry(entry), newSlot]
    subjects[idx] = withTeacherEntryMeta(teacher, {
      ...entry,
      subject: teacher.subject || '',
      subjectLabel,
      slots: slots.length ? slots : [newSlot]
    })
  } else {
    subjects.push(withTeacherEntryMeta(teacher, { subject: teacher.subject || '', subjectLabel, slots: [newSlot] }))
  }
  return { subjects }
}

/** Usuwa z additionalData wpisy slotów przypisane do danego nauczyciela (np. po odpięciu nauczyciela od ucznia). */
export const removeTeacherEntriesFromAdditionalData = (student, teacher, schoolSubjects) => {
  const { subjects } = parseAdditionalData(student)
  const filtered = subjects.filter(
    entry => !additionalSubjectEntryMatchesTeacher(entry, teacher, schoolSubjects)
  )
  return JSON.stringify({ subjects: filtered })
}

export const removeSlotForTeacher = (current, teacher, slotIndex, schoolSubjects) => {
  const subjects = [...(current?.subjects || [])]
  const subjectLabel = getResolvedSubjectLabel(teacher, schoolSubjects)
  const idx = findEntryIndexForTeacher(subjects, teacher, schoolSubjects)

  if (idx >= 0) {
    const entry = subjects[idx]
    const slots = getSlotsFromEntry(entry)
    if (slots.length <= 1) return { subjects }
    const newSlots = slots.filter((_, i) => i !== slotIndex)
    subjects[idx] = withTeacherEntryMeta(teacher, {
      ...entry,
      subject: teacher.subject || '',
      subjectLabel,
      slots: newSlots
    })
  }
  return { subjects }
}
